import { apiReferralValidation } from '@api/apiCall';
import ControlledTooltip from '@atoms/CustomToolTip';
import ShimmerReferralInput from '@atoms/Shimmers/ShimmerReferralInput';
import { Text, View } from '@components/primitives';
import { useToast } from '@hooks';
import { useIsFocused, useNavigation } from '@react-navigation/native';
import { Icon } from '@rneui/base';
import { getStore } from '@store/storeUtils';
import { useMutation } from '@tanstack/react-query';
import colors from '@theme/colors';
import { scale } from '@utils/normalize';
import { showErrorOnAPI } from '@utils/utils';
import { TFunction } from 'i18next';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import logReferralApplicable from './analyticsReferral';
import {
  shouldShowToastOnSuccess,
  shouldShowValidatedButton,
  shouldShowValidatedReferralInput,
} from './referralInputUtils';
import styles from './styles';

interface ReferralInputProps {
  isLoading?: boolean;
  selectedAccount?: {
    accountTypeId: number;
  };
  pageSource: string;
}

interface StoreState {
  validatedReferralCode: string;
  canShowReferral: boolean;
  referralValidated: boolean;
  setReferralValidated: (value: boolean) => void;
  setLoading: (value: boolean) => void;
  setInitialData: () => void;
  setValidatedReferralCode: (code: string) => void;
  setDeepLinkData: (data: string) => void;
  deepLinkData: string;
}

function ReferralInput({ isLoading = false, selectedAccount = { accountTypeId: 0 }, pageSource }: ReferralInputProps) {
  const navigation = useNavigation();
  const isFocused = useIsFocused();
  const toast = useToast();
  const { t } = useTranslation('common');

  const {
    validatedReferralCode,
    canShowReferral,
    referralValidated,
    setReferralValidated,
    setLoading,
    setInitialData,
    setValidatedReferralCode,
    setDeepLinkData,
    deepLinkData,
  } = getStore() as StoreState;

  const [referralCode, setReferralCode] = useState<string>('');

  const {
    isLoading: isValidationLoading,
    isError,
    error,
    isSuccess,
    data,
    mutate: validateReferralCodeApi,
  } = useMutation({
    mutationFn: (referralCodeInput: string) => apiReferralValidation(referralCodeInput),
  });

  useEffect(() => {
    if (canShowReferral && selectedAccount.accountTypeId === 0 && isFocused) {
      logReferralApplicable({ source: pageSource });
    }
  }, [canShowReferral, selectedAccount.accountTypeId, pageSource, isFocused]);

  useEffect(() => {
    setLoading(isValidationLoading);
  }, [isValidationLoading]);

  useEffect(() => {
    showErrorOnAPI(isError, error, toast, t, navigation, setInitialData, referralCode, pageSource);
  }, [isError, error]);

  useEffect(() => {
    shouldShowToastOnSuccess({
      isSuccess,
      data,
      toast,
      setReferralValidated,
      setValidatedReferralCode,
      referralCode,
      pageSource,
      t,
    });
  }, [isSuccess, data]);

  useEffect(() => {
    if (canShowReferral && deepLinkData && deepLinkData !== validatedReferralCode) {
      setReferralCode(deepLinkData);
      validateReferralCodeApi(deepLinkData);
      setDeepLinkData('');
    }
  }, [canShowReferral, deepLinkData, validatedReferralCode]);

  if (isLoading) {
    return <ShimmerReferralInput />;
  }

  if (canShowReferral && selectedAccount.accountTypeId === 0) {
    return (
      <View style={styles.referralParent}>
        <View flexDirection="row" alignItems="center">
          <Text testID="txtReferralHeader" variant="md-bold" color={colors.neutral.darkMist} marginRight="5px">
            {t('referral.referralHeader')}
          </Text>
          <ControlledTooltip
            translateString="referral.toolTip"
            logParams={{
              source: pageSource,
              referral_code: referralCode,
            }}>
            <Icon name="questioncircle" type="antdesign" size={scale(16)} color={colors.neutral.darkMist} />
          </ControlledTooltip>
        </View>
        <View style={styles.referralInputContainer}>
          {shouldShowValidatedReferralInput({
            t,
            referralValidated,
            setReferralCode,
            validatedReferralCode,
            pageSource,
          })}
          {shouldShowValidatedButton({
            referralValidated,
            referralCode,
            validateReferralCodeApi,
            pageSource,
          })}
        </View>
      </View>
    );
  }

  return null;
}

export default ReferralInput;
